import { SeverityNumber } from "@opentelemetry/api-logs";

import { SessionManager } from "./sessions/SessionManager";
import { ProviderType } from "./types";
import { error as logError, forceFlushLogs, log, warn } from "./utils/logger";
import { ViewRootSpanManager } from "./view";

const EXIT_ALERT_RULE = "l9rum-exit-lifecycle-failure";

class ExitManager {
  private provider: ProviderType;
  private viewManager: ViewRootSpanManager;
  private sessionManager: SessionManager;
  private isShuttingDown: boolean = false;
  private listenersAttached: boolean = false;

  private emitExitLog(
    level: "info" | "warn" | "error",
    message: string,
    trigger?: string,
    attributes: Record<string, unknown> = {},
    err?: unknown,
  ) {
    const payload = {
      message,
      attributes: {
        ...(trigger ? { "l9rum.exit.trigger": trigger } : {}),
        ...attributes,
      },
      ...(err ? { error: err } : {}),
    };

    if (level === "warn") {
      warn(payload);
      return;
    }
    if (level === "error") {
      logError(payload);
      return;
    }
    log(payload);
  }

  private flushOnExit(trigger: string) {
    try {
      const maybePromise = (this.provider as any)?.forceFlush?.();
      if (maybePromise && typeof (maybePromise as Promise<void>).catch === "function") {
        (maybePromise as Promise<void>).catch((e) => {
          this._logExitError(trigger, "traces.force_flush", e);
        });
      }
    } catch (e) {
      this._logExitError(trigger, "traces.force_flush", e);
    }

    const logFlush = forceFlushLogs({ beacon: true });
    if (logFlush && typeof (logFlush as Promise<void>).catch === "function") {
      (logFlush as Promise<void>).catch((err) => {
        this._logExitError(trigger, "logs.force_flush", err);
      });
    }
  }
  private pageHideListener?: (event: Event) => void;
  private freezeListener?: () => void;
  private beforeUnloadListener?: () => void;

  constructor(
    provider: ProviderType,
    viewManager: ViewRootSpanManager,
    sessionManager: SessionManager,
  ) {
    this.provider = provider;
    this.viewManager = viewManager;
    this.sessionManager = sessionManager;
  }

  public init() {
    if (typeof document === "undefined") {
      this.emitExitLog("warn", "[L9RUM Exit] Exit manager initialised in non-browser context", undefined, {
        "l9rum.exit.phase": "init",
        "l9rum.exit.guard": "no_document",
      });
      return;
    }

    if (this.listenersAttached) {
      this.emitExitLog(
        "warn",
        "[L9RUM Exit] init called more than once; ignoring",
        undefined,
        {
          "l9rum.exit.phase": "init",
          "l9rum.exit.guard": "duplicate_init",
        },
      );
      return;
    }
    this.listenersAttached = true;

    this.emitExitLog("info", "[L9RUM Exit] Initialising exit listeners", undefined, {
      "l9rum.exit.phase": "init",
    });

    // Secondary: pagehide for true unload (not BFCache)
    this.pageHideListener = (event: Event) => {
      const pageEvent = event as PageTransitionEvent;
      if (!pageEvent.persisted) {
        this._handleExit("pagehide");
      } else {
        this.emitExitLog("info", "[L9RUM Exit] pagehide (BFCache) detected; skipping exit", "pagehide", {
          "l9rum.exit.phase": "skip",
          "l9rum.exit.guard": "bfcache",
        });
      }
    };
    window.addEventListener(
      "pagehide",
      this.pageHideListener as any,
      {
        passive: true,
        capture: true,
      } as any,
    );
    this.emitExitLog("info", "[L9RUM Exit] pagehide listener attached", "pagehide", {
      "l9rum.exit.phase": "init",
    });

    // Tertiary: Page Lifecycle API 'freeze' (Chromium)
    if ("onfreeze" in document) {
      this.freezeListener = () => {
        this._handleExit("freeze");
      };
      document.addEventListener(
        "freeze",
        this.freezeListener as any,
        {
          passive: true,
          capture: true,
        } as any,
      );
      this.emitExitLog("info", "[L9RUM Exit] freeze listener attached", "freeze", {
        "l9rum.exit.phase": "init",
      });
    }

    // Safari desktop fallback: beforeunload
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isSafari && !isMobile) {
      this.beforeUnloadListener = () => {
        this._handleExit("beforeunload");
      };
      window.addEventListener(
        "beforeunload",
        this.beforeUnloadListener as any,
        {
          capture: true,
        } as any,
      );
      this.emitExitLog(
        "info",
        "[L9RUM Exit] beforeunload listener attached (Safari desktop)",
        "beforeunload",
        {
          "l9rum.exit.phase": "init",
          "l9rum.exit.guard": "safari_desktop",
        },
      );
    }
  }

  public destroy() {
    if (this.pageHideListener) {
      window.removeEventListener("pagehide", this.pageHideListener as any);
    }
    if (this.freezeListener) {
      document.removeEventListener("freeze", this.freezeListener as any);
    }
    if (this.beforeUnloadListener) {
      window.removeEventListener("beforeunload", this.beforeUnloadListener as any);
    }
    this.listenersAttached = false;
    this.emitExitLog("info", "[L9RUM Exit] Exit listeners removed", undefined, {
      "l9rum.exit.phase": "destroy",
    });
  }

  private _handleExit(trigger: string) {
    if (this.isShuttingDown) {
      this.emitExitLog("warn", "[L9RUM Exit] Exit sequence already running; ignoring trigger", trigger, {
        "l9rum.exit.phase": "skip",
        "l9rum.exit.guard": "reentrant",
      });
      return;
    }
    this.isShuttingDown = true;

    this.emitExitLog("info", "[L9RUM Exit] Exit sequence started", trigger, {
      "l9rum.exit.phase": "start",
    });

    // Non-blocking sequence to survive reload/close teardown
    try {
      this.viewManager.endForExit();
    } catch (e) {
      this._logExitError(trigger, "view.end", e);
    }
    try {
      this.sessionManager.end("exit");
    } catch (e) {
      this._logExitError(trigger, "session.end", e);
    }

    // Ensure any batched spans are exported even if 'hidden' didn't fire.
    // The exporter switches to Beacon mode on exit-related events.
    this.emitExitLog("info", "[L9RUM Exit] Forcing telemetry flush", trigger, {
      "l9rum.exit.phase": "force_flush",
    });
    this.flushOnExit(trigger);

    this.emitExitLog("info", "[L9RUM Exit] Exit flush dispatched", trigger, {
      "l9rum.exit.phase": "force_flush_dispatched",
    });

    // Allow future exits after a short window
    setTimeout(() => {
      this.isShuttingDown = false;
      this.emitExitLog("info", "[L9RUM Exit] Exit guard reset", trigger, {
        "l9rum.exit.phase": "reset",
      });
    }, 2000);
  }

  private _logExitError(trigger: string, phase: string, err: unknown) {
    logError({
      message: "[L9RUM Exit] Lifecycle error",
      attributes: {
        "l9rum.exit.trigger": trigger,
        "l9rum.exit.phase": phase,
        "log.alert.rule": EXIT_ALERT_RULE,
      },
      error: err,
      severityNumber: SeverityNumber.ERROR4,
      severityText: SeverityNumber[SeverityNumber.ERROR4] || "ERROR4",
    });
  }
}

export default ExitManager;
